/**
 * Singapore PayNow / EMVCo SGQR payload (UEN, fixed amount, bill reference).
 * Spec: EMVCo QR (MPM), PayNow proxy type 2 = UEN, sub-tag 03 "0" = amount locked.
 *
 * Library note: only this string is fed into QR renderers (qrcode / qrcode.react).
 */

/** CRC16-CCITT-FALSE per ISO/IEC 13239 — polynomial 0x1021, init 0xFFFF, refin/refout false (MSB-first per byte). */
export function emvCoQrCrc16(payloadWithoutCrc: string): string {
  let crc = 0xffff;
  for (let i = 0; i < payloadWithoutCrc.length; i++) {
    crc ^= payloadWithoutCrc.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, "0");
}

function tlv(tag: string, value: string): string {
  if (tag.length !== 2) throw new Error(`[PayNow SGQR] tag must be 2 chars, got ${tag.length}`);
  const len = value.length;
  if (len > 99) throw new Error(`[PayNow SGQR] TLV value too long for 2-digit length: ${len}`);
  return `${tag}${String(len).padStart(2, "0")}${value}`;
}

/** EMV field length limits (MPM). */
const MAX_BILL = 25;
const MAX_MCH = 25;
const MAX_CITY = 15;

function clampAscii(s: string, max: number): string {
  const noCtrl = s.replace(/[\u0000-\u001F\u007F]/g, "").trim();
  const ascii = noCtrl
    .split("")
    .map((c) => (c.charCodeAt(0) <= 0x7f ? c : ""))
    .join("");
  if (ascii.length <= max) return ascii;
  return ascii.slice(0, max);
}

function formatTransactionAmountSgd(amount: number): string {
  if (!Number.isFinite(amount) || amount < 0) return "0.00";
  return amount.toFixed(2);
}

function shouldLogPayNowPayload(): boolean {
  return typeof process !== "undefined" && process.env["LOG_PAYNOW_QR"] === "1";
}

export type BuildPayNowUenFixedAmountArgs = {
  uen: string;
  amountSgd: number;
  /** Bill / reference (tag 62-01). */
  billNumber: string;
  merchantName?: string;
  merchantCity?: string;
};

/**
 * Build full EMVCo MPM string for PayNow UEN with **fixed** amount and bill number.
 * - Proxy: UEN (type 2 in PayNow subfields)
 * - Editable amount: "0" (not editable)
 * - Country 58: SG, currency 53: 702 (SGD)
 */
export function buildPayNowUenFixedAmountPayload(args: BuildPayNowUenFixedAmountArgs): string {
  const uen = args.uen.replace(/\s/g, "").toUpperCase();
  const bill = clampAscii(args.billNumber, MAX_BILL) || "REF";
  const merchantName = clampAscii(args.merchantName ?? "CLARITY AV PTE LTD", MAX_MCH);
  const merchantCity = clampAscii(args.merchantCity ?? "Singapore", MAX_CITY);
  const amountStr = formatTransactionAmountSgd(args.amountSgd);

  const merchantAccountInfo =
    tlv("26", tlv("00", "SG.PAYNOW") + tlv("01", "2") + tlv("02", uen) + tlv("03", "0") + tlv("04", "20991231"));
  const additionalData = tlv("62", tlv("01", bill));

  const body =
    tlv("00", "01") +
    tlv("01", "12") +
    merchantAccountInfo +
    tlv("52", "0000") +
    tlv("53", "702") +
    tlv("54", amountStr) +
    tlv("58", "SG") +
    tlv("59", merchantName) +
    tlv("60", merchantCity) +
    additionalData;

  const withoutCrc = body + "6304";
  const crc = emvCoQrCrc16(withoutCrc);
  const full = withoutCrc + crc;

  if (shouldLogPayNowPayload()) {
    const logPayload = {
      payNowType: "UEN",
      amountMode: "fixed",
      editableAmountFlag: "0",
      pointOfInitiation: "12",
      uen,
      amountSgd: amountStr,
      billNumber: bill,
      merchantName,
      merchantCity,
      rawPayloadLength: full.length,
      rawPayload: full,
    };
    console.info("[PayNow SGQR] encoded payload", logPayload);
  }

  return full;
}
